import type { ProvisioningStatus, UserAction } from '@bonsai/shared';

import { apiFetch } from '../lib/apiFetch';

import type { CurrentRate } from './staff-pay-rates';
import type { RoleKey } from './users';
import type { CrewPosition } from './workshops';

export interface StaffAuditEntry {
  id: string;
  actorUserId: string;
  actorEmail: string | null;
  changedAt: string;
  fieldChanges: Record<string, { before: unknown; after: unknown }> | unknown;
}

export interface StaffMember {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  profilePhotoUrl: string | null;
  isActive: boolean;
  region: string | null;
  expertiseLevel: string | null;
  workshopCount: number;
  upcomingWorkshops: number;
  // StaffProfile fields surfaced on list responses.
  displayName?: string | null;
  fareharborUsername?: string | null;
  hireDate?: string | null;
  notes?: string | null;
  /** Phase 1.5+: regions this staff member belongs to (flat set — no
   *  primary designation). Empty array when unassigned. */
  regions?: Array<{ id: string; name: string; code: string | null; parentId: string | null }>;
  /** Auth0 role keys (BON-105). Empty array when the user has no Auth0
   *  account yet or holds no configured roles. Strings rather than a union
   *  here so the API client stays decoupled from the role-taxonomy module;
   *  the view-model normalizes them. */
  roles?: string[];
  /** Both present only when the viewer holds `read:pay_rates`. */
  isSalaried?: boolean;
  currentRates?: CurrentRate[];
  /** Where the row sits in its invite lifecycle. */
  provisioningStatus: ProvisioningStatus;
  invitedAt: string | null;
  /** Whether an Auth0 account is bound to this row. */
  hasAuth0: boolean;
  /** What the viewer may do to this row, as decided by the API. */
  allowedActions: UserAction[];
}

export interface WorkshopAssignment {
  id: string;
  title: string;
  venueName: string;
  /** Absolute UTC instant. Sort/compare only — never displayed (BON-386). */
  startDatetime: string;
  endDatetime: string;
  /** Venue-local wall clock, naive ISO with NO trailing `Z`. Display reads
   *  this; `startDatetime` shows the wrong day for an evening workshop in a
   *  western zone. Null when the warehouse has no local value. */
  localStartDatetime: string | null;
  status: string;
  position: string;
}

/** Legacy shape kept for the CurrentStaff response (assignStaffRegion etc.).
 *  The new staff detail response uses the flat `regions` array on
 *  StaffMember instead. */
export interface RegionEntry {
  id: string;
  staffId: string;
  regionId: string;
  isPrimary: boolean;
  createdAt: string;
  region: { id: string; name: string; code: string | null; parentId: string | null };
}

export interface StaffMemberDetail extends StaffMember {
  /** BON-398 — crew positions this person is CLEARED to work; the values the
   *  `qualifiedPositions` PATCH field replaces. */
  qualifiedPositions?: string[];
  bio: string | null;
  workshops: WorkshopAssignment[];
  // Phase 1.5+
  audit?: StaffAuditEntry[];
  profilePhotoUrl: string | null;
}

/** Editable fields for `PATCH /api/v1/staff/:id`. Mirrors backend StaffPatch. */
export type StaffPatch = Partial<{
  firstName: string;
  lastName: string;
  email: string;
  displayName: string | null;
  fareharborUsername: string | null;
  phone: string | null;
  hireDate: string | null;
  bio: string | null;
  profilePhotoUrl: string | null;
  notes: string | null;
  isActive: boolean;
  /** Full replacement of the staff_regions bridge — UUIDs of every region
   *  this staff member should belong to after save. `[]` clears all. */
  regionIds: string[];
  /** Full replacement of the staff_qualifications bridge (BON-398) — every
   *  crew position this person is cleared to work after save. `[]` clears all.
   *  Typed as CrewPosition so a value the backend enum rejects can't compile.
   *
   *  BON-419 — same name as the read field it replaces. It used to be
   *  `positions`, which collided with the always-empty rate stub. */
  qualifiedPositions: CrewPosition[];
}>;

export interface UpdateStaffResult {
  staff: StaffMemberDetail;
}

export interface WageLineItem {
  staffName: string;
  staffEmail: string;
  contractorId: string | null;
  staffType: string;
  position: string;
  workshopTitle: string;
  venue: string;
  date: string;
  rate: number;
  wages: number;
}

export interface WageSummary {
  name: string;
  email: string;
  contractorId: string;
  staffType: string;
  positions: string[];
  totalWages: number;
  workshopCount: number;
}

export interface WageReport {
  report: {
    startDate: string;
    endDate: string;
    totalWages: number;
    staffCount: number;
    contractorCount: number;
    workshopCount: number;
    staffedWorkshops: number;
    unstaffedWorkshops: number;
  };
  summary: WageSummary[];
  lineItems: WageLineItem[];
}

/** What the viewer may do on the Users page, as decided by the API. */
export interface UserManagementAccess {
  canInvite: boolean;
  grantableRoles: RoleKey[];
  assignableRegionIds: string[];
  /** True when the viewer only manages users in their own regions. */
  confinedToRegions: boolean;
}

export interface CurrentStaff {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  bio: string | null;
  profilePhotoUrl: string | null;
  isActive: boolean;
  regions: RegionEntry[];
  /** The assigned regions plus every region under them — what the caller may edit. */
  scopeRegionIds?: string[];
  userManagement: UserManagementAccess;
}

// ─── Current User ─────────────────────────────────────────────────────────────

export async function fetchCurrentStaff(): Promise<CurrentStaff> {
  const res = await apiFetch('/api/v1/staff/me');
  if (!res.ok) throw new Error(`Failed to fetch current user: ${res.status}`);
  return (await res.json()).user;
}

/** Replace the CALLER's own region assignments (BON-389, admin-gated
 *  server-side). Returns the updated assignments. */
export async function updateMyRegions(regionIds: string[]): Promise<RegionEntry[]> {
  const res = await apiFetch('/api/v1/staff/me/regions', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ regionIds }),
  });
  if (!res.ok) throw new Error(`Failed to update regions: ${res.status}`);
  return (await res.json()).regions;
}

// ─── Staff List & Detail ───────────────────────────────────────────────────────

/** Filters for `GET /api/v1/staff`. `regionId` scopes the list to staff with a
 *  `staffRegions` row in that region (BON-200); the backend intersects it with
 *  the caller's own scope, so it can only narrow, never widen. */
export interface ListStaffParams {
  regionId?: string;
}

export async function fetchStaff(params: ListStaffParams = {}): Promise<StaffMember[]> {
  const qs = new URLSearchParams();
  if (params.regionId !== undefined) qs.set('regionId', params.regionId);
  const query = qs.toString();
  const res = await apiFetch(`/api/v1/staff${query ? `?${query}` : ''}`);
  if (!res.ok) throw new Error(`Failed to fetch staff: ${res.status}`);
  return (await res.json()).staff;
}

export async function fetchStaffMember(userId: string): Promise<StaffMemberDetail> {
  const res = await apiFetch(`/api/v1/staff/${userId}`);
  if (!res.ok) throw new Error(`Failed to fetch staff member: ${res.status}`);
  return (await res.json()).staff;
}

// ─── Profile ──────────────────────────────────────────────────────────────────

/** Thrown when the backend returns a 400 with a `fieldErrors` map (Zod
 *  validation failure). UI surfaces messages inline next to each field. */
export class StaffValidationError extends Error {
  fieldErrors: Record<string, string>;
  constructor(fieldErrors: Record<string, string>, message = 'Validation failed') {
    super(message);
    this.name = 'StaffValidationError';
    this.fieldErrors = fieldErrors;
  }
}

/** Codes a `PATCH /api/v1/staff/:id` 409 can carry. */
export type StaffConflictCode = 'EMAIL_LOCKED';

/** Thrown when the backend refuses a patch with a 409 and a stable `code`. */
export class StaffConflictError extends Error {
  constructor(
    public readonly code: StaffConflictCode | string,
    message: string,
  ) {
    super(message);
    this.name = 'StaffConflictError';
  }
}

interface StaffPatchErrorBody {
  error?: string;
  message?: string;
  code?: string;
  fieldErrors?: Record<string, string>;
}

/** The typed error for a failed `PATCH /staff/:id`: per-field on a 400,
 *  coded on a 409, plain otherwise. */
function staffPatchError(status: number, body: StaffPatchErrorBody): Error {
  if (status === 400 && body.fieldErrors) {
    return new StaffValidationError(body.fieldErrors, body.message ?? 'Validation failed');
  }
  if (status === 409 && body.code) {
    return new StaffConflictError(body.code, body.message ?? body.error ?? 'Conflict');
  }
  return new Error(body.error || body.message || `Failed to update profile: ${status}`);
}

export async function updateStaffProfile(
  userId: string,
  updates: StaffPatch,
): Promise<UpdateStaffResult> {
  const res = await apiFetch(`/api/v1/staff/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as StaffPatchErrorBody;
    throw staffPatchError(res.status, body);
  }
  return res.json();
}

// Pay rates (the rate history, the salaried flag) live in ./staff-pay-rates.

// ─── Regions ──────────────────────────────────────────────────────────────────

export async function assignStaffRegion(
  userId: string,
  regionId: string,
  isPrimary = false,
): Promise<RegionEntry> {
  const res = await apiFetch(`/api/v1/staff/${userId}/regions`, {
    method: 'POST',
    body: JSON.stringify({ regionId, isPrimary }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || `Failed to assign region: ${res.status}`);
  }
  return (await res.json()).region;
}

export async function removeStaffRegion(userId: string, regionId: string): Promise<void> {
  const res = await apiFetch(`/api/v1/staff/${userId}/regions/${regionId}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`Failed to remove region: ${res.status}`);
}

// ─── Wage Report ───────────────────────────────────────────────────────────────

export async function fetchWageReport(startDate: string, endDate: string): Promise<WageReport> {
  const res = await apiFetch(
    `/api/v1/staff/reports/wages?startDate=${startDate}&endDate=${endDate}`,
  );
  if (!res.ok) throw new Error(`Failed to fetch wage report: ${res.status}`);
  return res.json();
}
